// Build human/LLM-readable Markdown documents for a workout, a program, or a single
// exercise — so a coach or an LLM can read a plan and suggest improvements. Plan-only by
// default; pass a `perf` map (library_exercise_id → recent sessions, from api.exerciseHistory)
// to append a "Recent performance" block under each exercise. Unit-aware via lib/format.js.
import { muscleName } from "../store.js";
import { fmtWeight, fmtDuration, fmtDistance, fmtPace, epley1RM, fmtDate } from "./format.js";
import { setSummaryText, supersetLabels } from "../components/common.js";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Reps prescription as a string ("8–12", "10", or null when none).
function repsRange(ex) {
  const lo = ex.target_reps_min, hi = ex.target_reps_max;
  if (lo && hi) return lo === hi ? `${lo}` : `${lo}–${hi}`;
  return lo ? `${lo}` : hi ? `${hi}` : null;
}

// One-line prescription for an exercise, shaped by its metric type.
function targetText(ex) {
  const sets = ex.target_sets ?? null;
  const setsPart = sets ? `${sets} × ` : "";
  switch (ex.metric_type) {
    case "duration":
      return ex.target_duration_seconds ? `${setsPart}${fmtDuration(ex.target_duration_seconds)}` : (sets ? `${sets} sets` : "—");
    case "distance_duration": {
      const bits = [];
      if (ex.target_distance_m != null) bits.push(fmtDistance(ex.target_distance_m));
      if (ex.target_duration_seconds != null) bits.push(fmtDuration(ex.target_duration_seconds));
      const pace = fmtPace(ex.target_duration_seconds, ex.target_distance_m);
      const body = bits.join(" in ") || (sets ? "" : "—");
      return `${setsPart}${body}${pace ? ` (${pace})` : ""}`.trim() || "—";
    }
    case "bodyweight_reps": {
      const reps = repsRange(ex);
      return `${setsPart}${reps ? `${reps} reps` : "reps"} (bodyweight)`;
    }
    default: {
      const reps = repsRange(ex);
      const w = ex.target_weight_kg != null ? ` @ ${fmtWeight(ex.target_weight_kg)}` : "";
      return `${setsPart}${reps ? `${reps} reps` : "reps"}${w}`;
    }
  }
}

function musclesLine(ex) {
  const prim = (ex.primary_muscle_ids || []).map(muscleName).filter(Boolean);
  const sec = (ex.secondary_muscle_ids || []).map(muscleName).filter(Boolean);
  if (!prim.length && !sec.length) return null;
  return prim.join(", ") + (sec.length ? ` (secondary: ${sec.join(", ")})` : "");
}

// Recent-performance lines for one exercise from its history sessions (newest first, capped).
function perfLines(sessions, metricType) {
  const out = [];
  for (const s of sessions.slice(0, 3)) {
    const working = (s.sets || []).filter((x) => !x.is_warmup);
    if (!working.length) continue;
    const summary = working.map((set) => setSummaryText(metricType, set, { rpe: true })).join(", ");
    const best = working.reduce((m, x) => Math.max(m, epley1RM(x.weight_kg || 0, x.reps || 0)), 0);
    const e1rm = best > 0 ? ` — est. 1RM ${fmtWeight(Math.round(best * 10) / 10)}` : "";
    out.push(`  - ${fmtDate(s.date)}: ${summary}${e1rm}`);
  }
  return out.length ? ["- Recent performance:", ...out] : [];
}

// The per-exercise blocks of a workout (no title), at the given heading depth. `perf` optional.
function workoutExerciseBlocks(workout, heading, perf) {
  const exercises = workout.exercises || [];
  const labels = supersetLabels(exercises);
  const lines = [];
  exercises.forEach((ex, i) => {
    const ss = labels[i];
    const tag = ss ? ` _(superset ${ss.label})_` : "";
    lines.push(`${heading} ${i + 1}. ${ex.exercise_name}${tag}`);
    lines.push(`- Target: ${targetText(ex)}`);
    if (ex.equipment) lines.push(`- Equipment: ${ex.equipment}`);
    const muscles = musclesLine(ex);
    if (muscles) lines.push(`- Muscles: ${muscles}`);
    if (ex.rest_seconds != null) lines.push(`- Rest: ${fmtDuration(ex.rest_seconds)}`);
    if (ex.notes) lines.push(`- Notes: ${ex.notes}`);
    if (perf) lines.push(...perfLines(perf.get(ex.library_exercise_id) || [], ex.metric_type));
    lines.push("");
  });
  return lines;
}

/** Markdown for a single workout template. */
export function workoutToMarkdown(workout, { perf } = {}) {
  const lines = [`# ${workout.name}`, ""];
  if (workout.description) lines.push(workout.description, "");
  if (workout.notes) lines.push(`_Notes: ${workout.notes}_`, "");
  const n = (workout.exercises || []).length;
  lines.push(`**${n} exercise${n === 1 ? "" : "s"}**`, "");
  lines.push(...workoutExerciseBlocks(workout, "##", perf));
  lines.push("_Exported from Kashtu._");
  return lines.join("\n");
}

/** Markdown for a multi-day program. `workoutsById` maps workout id → full workout (with exercises). */
export function programToMarkdown(program, workoutsById, { perf } = {}) {
  const days = program.days || [];
  const lines = [`# ${program.name}`, ""];
  if (program.description) lines.push(program.description, "");
  lines.push(`**${days.length}-day schedule**`, "");
  days.forEach((d, i) => {
    const wo = d.workout ? workoutsById.get(d.workout.id) : null;
    const title = d.is_rest || !wo ? `${d.label || "Rest"} (rest day)` : (d.label ? `${d.label} — ${wo.name}` : wo.name);
    lines.push(`## Day ${i + 1}: ${title}`, "");
    if (wo) lines.push(...workoutExerciseBlocks(wo, "###", perf));
  });
  lines.push("_Exported from Kashtu._");
  return lines.join("\n");
}

/** Markdown for a single library exercise (full detail incl. instructions). `history` optional. */
export function exerciseToMarkdown(exercise, { history } = {}) {
  const lines = [`# ${exercise.exercise_name}`, ""];
  if (exercise.short_description) lines.push(exercise.short_description, "");
  const meta = [exercise.equipment, exercise.level, exercise.mechanic, exercise.force, exercise.category].filter(Boolean).map(cap);
  if (meta.length) lines.push(`- ${meta.join(" · ")}`);
  lines.push(`- Logged as: ${exercise.metric_type || "weight_reps"}`);
  const muscles = musclesLine(exercise);
  if (muscles) lines.push(`- Muscles: ${muscles}`);
  if (exercise.video_url) lines.push(`- Demo: ${exercise.video_url}`);
  lines.push("");
  if (exercise.instructions?.length) {
    lines.push("## Instructions", "");
    exercise.instructions.forEach((step, i) => lines.push(`${i + 1}. ${step}`));
    lines.push("");
  }
  if (history?.length) {
    lines.push("## Recent performance", "");
    lines.push(...perfLines(history, exercise.metric_type).slice(1)); // drop the "- Recent performance:" header line
    lines.push("");
  }
  lines.push("_Exported from Kashtu._");
  return lines.join("\n");
}
